/* eslint-disable complexity */
import { columnType, DataType } from 'components/table/types';
import { Stack, Switch, Typography, Box, TextField } from '@mui/material';
import { CustomSwitch } from 'components/Inputs';
import { systemSettingsNamesMapping } from 'constants/system-settings.constants';

const componentStyles = {
	// General container for text values
	valueContainer: {
		maxWidth: 300,
		width: 300,
		wordWrap: 'break-word' as const,
		overflowWrap: 'break-word' as const,
		whiteSpace: 'normal' as const,
	},
	// TextField styles
	textField: {
		'& .MuiInputBase-root': {
			width: 300,
			minWidth: 200,
			maxWidth: 300,
		},
	},
	textFieldWithButton: {
		width: 100, // Reduced width when next to an edit button
		'& .MuiInputBase-root': {
			width: 200,
		},
	},
	// Switch styles
	switch: {
		base: { width: '300px' },
		withButton: { width: '100px' }, // Reduced width when next to an edit button
	},
	// EditActions (pencil, check, cancel icons)
	editActions: {
		stack: {
			width: '25px',
			height: '25px',
		},
		icon: {
			cursor: 'pointer',
		},
		saveIcon: {
			cursor: 'pointer',
			opacity: 1,
		},
	},
	// Wrapper for content that has an edit button
	contentWithEditButton: {
		wrapperStack: {
			width: 100,
		},
		contentBox: {
			flex: '0 0 200px',
			maxWidth: 200,
			wordWrap: 'break-word' as const,
			overflowWrap: 'break-word' as const,
			whiteSpace: 'normal' as const,
		},
	},
};
interface IEditActionsProps {
	row: any;
	editPermissions: boolean;
	onSave: (row: any) => void;
	onCancel: () => void;
	onEdit: (id: string) => void;
}

interface IEditableFieldProps {
	value: any;
	row: any;
	element: string;
	editPermissions: boolean;
	updateModifiedItem: (element: string, value: any, row: any) => void;
	hasButton?: boolean;
}

export const tableData = (data: any[]) => {
	return data?.map((item: any) => ({ ...item }));
};

const getValueType = (value: any): string => {
	if (value === null || value === undefined) return 'empty';
	const stringValue = String(value).trim().toLowerCase();
	if (stringValue === '') return 'empty';
	if (stringValue === 'true' || stringValue === 'false') return 'boolean';
	if (/^-?\d+(\.\d+)?$/.test(stringValue)) return 'number';
	return 'string';
};

/**
 * Reusable component for edit actions (save/cancel/edit buttons)
 */
const EditActions = ({ row, editPermissions, onSave, onCancel, onEdit }: IEditActionsProps) => {
	if (!editPermissions) return null;

	if (row.editable) {
		return (
			<Stack gap={5} direction="row" sx={componentStyles.editActions.stack}>
				<img
					src="icons/check.svg"
					style={componentStyles.editActions.saveIcon}
					onClick={() => onSave(row)}
					alt="Save"
				/>
				<img src="icons/cancel.svg" style={componentStyles.editActions.icon} onClick={onCancel} alt="Cancel" />
			</Stack>
		);
	}

	return (
		<img
			src="icons/pencil.svg"
			style={componentStyles.editActions.icon}
			onClick={() => onEdit(row.id)}
			alt="Edit"
		/>
	);
};

/**
 * Reusable component for text field components
 */
const EditableTextField = ({
	value,
	row,
	element,
	editPermissions,
	updateModifiedItem,
	hasButton = false,
}: IEditableFieldProps) => {
	if (row.editable) {
		return (
			<TextField
				multiline
				rows={2}
				fullWidth={!hasButton}
				sx={hasButton ? componentStyles.textFieldWithButton : componentStyles.textField}
				value={value || ''}
				onChange={editPermissions ? (event) => updateModifiedItem(element, event.target.value, row) : undefined}
			/>
		);
	}

	return (
		<Box sx={!hasButton ? componentStyles.valueContainer : {}}>
			<Typography variant="body2">{value || '-'}</Typography>
		</Box>
	);
};

/**
 * Reusable function to create the value handler for each column
 */
const createValueHandler = (
	element: string,
	theme: any,
	editPermissions: boolean,
	updateModifiedItem: any,
	saveItemChanges: any,
	cancelItemChanges: any,
	handleEditButtonClick: any,
	editableColumns: string[],
	editIconColumns: string[]
) => {
	const commonProps = {
		editPermissions,
		updateModifiedItem,
		onSave: saveItemChanges,
		onCancel: cancelItemChanges,
		onEdit: handleEditButtonClick,
	};

	return (value: any, record: any) => {
		const hasEditButton = editIconColumns.includes(element);

		const content = (() => {
			// Non-editable columns just display text
			if (!editableColumns.includes(element)) {
				return (
					<Typography sx={componentStyles.valueContainer} variant="body2">
						{systemSettingsNamesMapping[value] || value || '-'}
					</Typography>
				);
			}

			// Handle boolean values with a Switch
			const isBoolean = getValueType(value) === 'boolean' || typeof value === 'boolean';
			if (isBoolean) {
				const isChecked = value === 'true' || value === true;
				return (
					<CustomSwitch
						checked={isChecked}
						theme={theme}
						sx={hasEditButton ? componentStyles.switch.withButton : componentStyles.switch.base}
					>
						<Switch
							checked={isChecked}
							disabled={!record.editable}
							onChange={
								editPermissions ? () => updateModifiedItem(element, !isChecked, record) : undefined
							}
						/>
					</CustomSwitch>
				);
			}

			// Handle string/number values with an EditableTextField
			return (
				<EditableTextField
					value={value}
					row={record}
					element={element}
					hasButton={hasEditButton}
					{...commonProps}
				/>
			);
		})();

		// If there's no edit button, just return the content
		if (!hasEditButton) {
			return content;
		}

		// If there is an edit button, wrap the content and the button in a Stack
		return (
			<Stack
				direction="row"
				justifyContent="space-between"
				gap={2}
				alignItems="center"
				sx={componentStyles.contentWithEditButton.wrapperStack}
			>
				<Box sx={componentStyles.contentWithEditButton.contentBox}>{content}</Box>
				<EditActions row={record} {...commonProps} />
			</Stack>
		);
	};
};

export const buildColumns = (
	t: any,
	handleEditButtonClick: any,
	saveItemChanges: any,
	cancelItemChanges: any,
	updateModifiedItem: any,
	editPermissions: boolean,
	theme: any,
	editableColumns: string[],
	editIconColumns: string[],
	tableColumns: Record<string, DataType>,
	translationPrefix: string
): columnType[] => {
	return Object.keys(tableColumns).map((element) => {
		const customValueHandle = {
			customValueHandle: createValueHandler(
				element,
				theme,
				editPermissions,
				updateModifiedItem,
				saveItemChanges,
				cancelItemChanges,
				handleEditButtonClick,
				editableColumns,
				editIconColumns
			),
		};

		return {
			fieldId: 'id',
			name: element,
			title: t(`${translationPrefix}.${element}`),
			type: tableColumns[element] as DataType,
			sortable: true,
			...customValueHandle,
		};
	});
};
